import { z } from 'zod';

const ALLOWED_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/jpg',
    'application/pdf',
    'text/markdown',
    'text/x-markdown',
    'text/md',
    'text/plain',
    '', // Some browsers report empty MIME type for certain files
];

const ALLOWED_EXTENSIONS = ['.pdf', '.md', '.txt', '.jpg', '.jpeg', '.png'];

// File attachment validation schema
export const fileAttachmentSchema = z
    .object({
        name: z.string().min(1, 'File name is required').max(255, 'File name too long'),
        size: z
            .number()
            .min(1, 'File size must be greater than 0')
            .max(10 * 1024 * 1024, 'File size too large'),
        type: z.string(),
        lastModified: z.number().positive('Invalid file timestamp'),
    })
    .refine(
        data => {
            // Check MIME type
            const isValidMimeType = ALLOWED_MIME_TYPES.includes(data.type);

            // Fallback to extension check if MIME type is empty or invalid
            const fileExtension = '.' + data.name.split('.').pop()?.toLowerCase();
            const isValidExtension = ALLOWED_EXTENSIONS.includes(fileExtension);

            return isValidMimeType || isValidExtension;
        },
        {
            message: 'Invalid file type',
            path: ['type'],
        }
    );

export const fileAttachmentArraySchema = z
    .array(fileAttachmentSchema)
    .max(10, 'Maximum 10 files allowed')
    .refine(files => {
        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        return totalSize <= 50 * 1024 * 1024; // 50MB total
    }, 'Total file size exceeds limit');

// Chat message validation schema
export const chatMessageSchema = z.object({
    content: z.string().min(1, 'Message cannot be empty').max(4000, 'Message too long'),
    attachments: fileAttachmentArraySchema.optional(),
});

// Product data validation schema - based on actual API response
export const productDataSchema = z.object({
    product: z.object({
        image: z.string(),
        title: z.string(),
        subtitle: z.string(),
        description: z.string(),
        download_link: z.string(),
        file_name: z.string(),
    }),
    button: z.object({
        image: z.string(),
    }),
    type: z.string(),
    id: z.number(),
    ulid: z.string(),
});

// Chat chunk validation schema
export const chatChunkSchema = z.object({
    type: z.enum(['marker', 'status', 'response', 'product', 'suggestions', 'links', 'code-block', 'display']),
    event: z.string().optional(),
    content: z.union([
        z.string(),
        productDataSchema,
        z.array(z.string()),
        z.array(
            z.object({
                title: z.string(),
                url: z.string().url(),
                description: z.string().optional(),
            })
        ),
        z.array(
            z.object({
                language: z.string(),
                code: z.string(),
            })
        ),
    ]),
});

// Feedback validation schema
export const feedbackSchema = z.object({
    messageId: z.union([z.string(), z.number()]),
    feedback: z.union([z.literal(1), z.literal(-1)]),
});

// Chat session validation schema
export const chatSessionSchema = z.object({
    id: z.union([z.string(), z.number()]),
    title: z.string().min(1, 'Chat title is required'),
    messages: z.array(z.any()), // ChatMessage schema would be recursive, so using any for now
    loading: z.boolean(),
    isStreaming: z.boolean(),
    abortController: z.any().nullable(), // AbortController type
    created_at: z.date().optional(),
    updated_at: z.date().optional(),
});

// Export types
export type FileAttachmentFormState = z.infer<typeof fileAttachmentSchema>;
export type FileAttachmentArrayFormState = z.infer<typeof fileAttachmentArraySchema>;
export type ChatMessageFormState = z.infer<typeof chatMessageSchema>;
export type ProductDataFormState = z.infer<typeof productDataSchema>;
export type ChatChunkFormState = z.infer<typeof chatChunkSchema>;
export type FeedbackFormState = z.infer<typeof feedbackSchema>;
export type ChatSessionFormState = z.infer<typeof chatSessionSchema>;
